import { useState, useEffect, useCallback, useRef } from "react";
import axios from "axios";
import { API, getAuthHeaders } from "../App";
import Layout from "../components/Layout";
import { useAppSettings } from "../contexts/AppSettingsContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Plus, Edit, Trash2, FileText, Save, Upload, Image as ImageIcon, Monitor, Smartphone, RotateCcw } from "lucide-react";
import { toast } from "sonner";

function BlueprintTypeSection({ types, onAdd, onEdit, onDelete }) {
  return (
    <div className="bg-white border border-[#E7E5E4] p-6" data-testid="blueprint-type-section">
      <div className="flex items-center justify-between mb-4">
        <div>
          <h2 className="font-heading font-bold text-xl text-[#1C1917]">Blueprint Types</h2>
          <p className="text-sm text-[#78716C] mt-1">Categories available when classifying blueprints (e.g., Mechanical, Electrical)</p>
        </div>
        <Button onClick={onAdd} className="bg-[#D97706] hover:bg-[#B45309] text-white rounded-none" data-testid="add-bp-type-button">
          <Plus size={16} className="mr-2" />ADD TYPE
        </Button>
      </div>
      {types.length === 0 ? (
        <p className="text-[#78716C] text-sm py-4">No blueprint types defined.</p>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-2">
          {types.map((t) => (
            <div key={t.id} className="flex items-center justify-between p-3 border border-[#E7E5E4] hover:border-[#1C1917] transition-colors" data-testid={`bp-type-row-${t.id}`}>
              <div className="flex items-center gap-3">
                <FileText size={16} className="text-[#78716C]" />
                <span className="text-[#1C1917] font-medium text-sm">{t.name}</span>
              </div>
              <div className="flex gap-1">
                <Button size="sm" variant="ghost" onClick={() => onEdit(t)} className="h-8 w-8 p-0" data-testid={`edit-bp-type-${t.id}`}><Edit size={14} /></Button>
                <Button size="sm" variant="ghost" onClick={() => onDelete(t)} className="h-8 w-8 p-0 text-[#DC2626]" data-testid={`delete-bp-type-${t.id}`}><Trash2 size={14} /></Button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

export default function BrandingSettingsPage({ onLogout, userRole, isFeatureEnabled }) {
  const { settings, refreshSettings } = useAppSettings();
  const [form, setForm] = useState({});
  const [saving, setSaving] = useState(false);
  const [bpTypes, setBpTypes] = useState([]);
  const [bpTypeDialogOpen, setBpTypeDialogOpen] = useState(false);
  const [editingBpType, setEditingBpType] = useState(null);
  const [bpTypeForm, setBpTypeForm] = useState({ name: "" });
  const [iconStatus, setIconStatus] = useState(null);
  const [iconUploading, setIconUploading] = useState(false);
  const iconInputRef = useRef(null);

  useEffect(() => {
    setForm({
      app_name: settings.app_name || "",
      app_tagline: settings.app_tagline || "",
      credit_line: settings.credit_line || "",
      primary_color: settings.primary_color || "#D97706",
      sidebar_bg: settings.sidebar_bg || "#1C1917",
      sidebar_border: settings.sidebar_border || "#292524",
      sidebar_text: settings.sidebar_text || "#A8A29E",
      sidebar_active: settings.sidebar_active || "#D97706",
      accent_color: settings.accent_color || "#EA580C",
      logo_url: settings.logo_url || "",
    });
  }, [settings]);

  const fetchBpTypes = useCallback(async () => {
    try {
      const resp = await axios.get(`${API}/admin/blueprint-types`, { headers: getAuthHeaders() });
      setBpTypes(resp.data || []);
    } catch { /* empty */ }
  }, []);

  useEffect(() => { fetchBpTypes(); }, [fetchBpTypes]);

  const fetchIconStatus = useCallback(async () => {
    try {
      const resp = await axios.get(`${API}/admin/icons/status`, { headers: getAuthHeaders() });
      setIconStatus(resp.data);
    } catch { /* may not be available */ }
  }, []);

  useEffect(() => { fetchIconStatus(); }, [fetchIconStatus]);

  const handleIconUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!file.type.startsWith("image/")) { toast.error("Please select an image file"); return; }
    setIconUploading(true);
    try {
      const formData = new FormData();
      formData.append("file", file);
      const resp = await axios.post(`${API}/admin/icons/upload`, formData, {
        headers: { ...getAuthHeaders(), "Content-Type": "multipart/form-data" },
      });
      toast.success(resp.data.message);
      fetchIconStatus();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Icon upload failed");
    } finally {
      setIconUploading(false);
      if (iconInputRef.current) iconInputRef.current.value = "";
    }
  };

  const handleResetIcons = async () => {
    if (!window.confirm("Reset all icons to defaults? This removes your custom icons.")) return;
    try {
      await axios.delete(`${API}/admin/icons`, { headers: getAuthHeaders() });
      toast.success("Icons reset to defaults");
      fetchIconStatus();
    } catch (err) { toast.error(err.response?.data?.detail || "Failed to reset icons"); }
  };

  const handleSaveBranding = async () => {
    setSaving(true);
    try {
      await axios.put(`${API}/admin/app-settings`, form, { headers: getAuthHeaders() });
      toast.success("Branding settings saved. Refresh the page to see full changes.");
      refreshSettings();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to save settings");
    } finally { setSaving(false); }
  };

  // Blueprint type CRUD
  const openAddBpType = () => { setEditingBpType(null); setBpTypeForm({ name: "" }); setBpTypeDialogOpen(true); };
  const openEditBpType = (t) => { setEditingBpType(t); setBpTypeForm({ name: t.name }); setBpTypeDialogOpen(true); };
  const handleBpTypeSubmit = async (e) => {
    e.preventDefault();
    try {
      if (editingBpType) {
        await axios.put(`${API}/admin/blueprint-types/${editingBpType.id}`, bpTypeForm, { headers: getAuthHeaders() });
        toast.success("Blueprint type updated");
      } else {
        await axios.post(`${API}/admin/blueprint-types`, bpTypeForm, { headers: getAuthHeaders() });
        toast.success("Blueprint type created");
      }
      setBpTypeDialogOpen(false);
      fetchBpTypes();
    } catch (err) { toast.error(err.response?.data?.detail || "Failed"); }
  };
  const handleDeleteBpType = async (t) => {
    if (!window.confirm(`Delete blueprint type "${t.name}"?`)) return;
    try {
      await axios.delete(`${API}/admin/blueprint-types/${t.id}`, { headers: getAuthHeaders() });
      toast.success(`Type "${t.name}" deleted`);
      fetchBpTypes();
    } catch (err) { toast.error(err.response?.data?.detail || "Failed"); }
  };

  const ColorField = ({ label, fieldKey }) => (
    <div>
      <Label className="text-[#1C1917] text-sm">{label}</Label>
      <div className="flex items-center gap-2 mt-1">
        <input type="color" value={form[fieldKey] || "#000000"} onChange={(e) => setForm({ ...form, [fieldKey]: e.target.value })} className="h-9 w-9 border-none cursor-pointer" data-testid={`color-${fieldKey}`} />
        <Input value={form[fieldKey] || ""} onChange={(e) => setForm({ ...form, [fieldKey]: e.target.value })} className="flex-1 rounded-none border-[#E7E5E4] font-mono text-xs" />
      </div>
    </div>
  );

  return (
    <Layout onLogout={onLogout} userRole={userRole} isFeatureEnabled={isFeatureEnabled}>
      <div className="space-y-6" data-testid="branding-settings-page">
        <div>
          <h1 className="font-heading font-black text-4xl text-[#1C1917] mb-2" data-testid="branding-title">Branding & Settings</h1>
          <p className="text-[#78716C] text-sm">Customize the application name, logo, colors, and document types</p>
        </div>

        {/* App Identity */}
        <div className="bg-white border border-[#E7E5E4] p-6" data-testid="identity-section">
          <h2 className="font-heading font-bold text-xl text-[#1C1917] mb-4">Application Identity</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <Label className="text-[#1C1917]">Application Name</Label>
              <Input value={form.app_name || ""} onChange={(e) => setForm({ ...form, app_name: e.target.value })} className="mt-1 rounded-none border-[#E7E5E4] font-mono" data-testid="app-name-input" />
            </div>
            <div>
              <Label className="text-[#1C1917]">Tagline / Subtitle</Label>
              <Input value={form.app_tagline || ""} onChange={(e) => setForm({ ...form, app_tagline: e.target.value })} className="mt-1 rounded-none border-[#E7E5E4]" data-testid="app-tagline-input" />
            </div>
            <div>
              <Label className="text-[#1C1917]">Credit Line</Label>
              <Input value={form.credit_line || ""} onChange={(e) => setForm({ ...form, credit_line: e.target.value })} className="mt-1 rounded-none border-[#E7E5E4]" data-testid="credit-line-input" />
            </div>
            <div>
              <Label className="text-[#1C1917]">Logo URL</Label>
              <Input value={form.logo_url || ""} onChange={(e) => setForm({ ...form, logo_url: e.target.value })} className="mt-1 rounded-none border-[#E7E5E4] font-mono text-xs" placeholder="/vault_logo.png" data-testid="logo-url-input" />
              {form.logo_url && <img src={form.logo_url} alt="Preview" className="h-10 mt-2 bg-[#1C1917] p-1" />}
            </div>
          </div>
        </div>

        {/* App Icons */}
        <div className="bg-white border border-[#E7E5E4] p-6" data-testid="icon-section">
          <div className="flex items-center justify-between mb-4">
            <div>
              <h2 className="font-heading font-bold text-xl text-[#1C1917]">App Icons</h2>
              <p className="text-sm text-[#78716C] mt-1">Upload a master image to auto-generate favicon, PWA icons, Apple touch icon, and Android maskable icons</p>
            </div>
            <div className="flex gap-2">
              {iconStatus?.has_custom_icons && (
                <Button variant="outline" onClick={handleResetIcons} className="rounded-none border-[#DC2626] text-[#DC2626] hover:bg-[#DC2626] hover:text-white" data-testid="reset-icons-btn">
                  <RotateCcw size={14} className="mr-1" /> Reset
                </Button>
              )}
              <Button onClick={() => iconInputRef.current?.click()} disabled={iconUploading} className="bg-[#D97706] hover:bg-[#B45309] text-white rounded-none" data-testid="upload-icon-btn">
                <Upload size={16} className="mr-2" />{iconUploading ? "Generating..." : "Upload Master Icon"}
              </Button>
              <input ref={iconInputRef} type="file" accept="image/*" onChange={handleIconUpload} className="hidden" data-testid="icon-file-input" />
            </div>
          </div>
          {iconStatus?.has_custom_icons ? (
            <div>
              <div className="flex items-center gap-2 mb-3">
                <span className="text-xs px-2 py-0.5 bg-[#F0FDF4] border border-[#10B981] text-[#10B981] font-bold">CUSTOM ICONS ACTIVE</span>
                {iconStatus.icon_updated_at && (
                  <span className="text-xs text-[#94A3B8]">Updated {new Date(iconStatus.icon_updated_at).toLocaleString()}</span>
                )}
              </div>
              <div className="grid grid-cols-4 md:grid-cols-8 gap-3">
                {iconStatus.icons?.filter(i => i.exists).map((icon) => (
                  <div key={icon.name} className="text-center">
                    <div className="bg-[#F8FAFC] border border-[#E2E8F0] p-2 flex items-center justify-center" style={{ minHeight: 60 }}>
                      <img src={`${icon.url}?t=${Date.now()}`} alt={icon.name} className="max-h-12 max-w-12" />
                    </div>
                    <p className="text-[9px] text-[#64748B] mt-1 font-mono truncate">{icon.name}</p>
                  </div>
                ))}
              </div>
            </div>
          ) : (
            <div className="border border-dashed border-[#E2E8F0] p-8 text-center">
              <ImageIcon size={32} className="mx-auto text-[#CBD5E1] mb-2" />
              <p className="text-sm text-[#94A3B8]">No custom icons uploaded. Using default icons.</p>
              <p className="text-xs text-[#CBD5E1] mt-1">Recommended: 1024x1024 PNG with transparent background</p>
            </div>
          )}
          <div className="mt-3 bg-[#F0F9FF] border border-[#BAE6FD] p-3">
            <p className="text-xs text-[#0369A1]">
              <strong>Auto-generated sizes:</strong> Favicon (16/32/48 + .ico), Apple Touch (180), PWA (192/512), Android Maskable (192/512). Total: 9 icon variants from one upload.
            </p>
          </div>
        </div>

        {/* Colors */}
        <div className="bg-white border border-[#E7E5E4] p-6" data-testid="colors-section">
          <h2 className="font-heading font-bold text-xl text-[#1C1917] mb-4">Theme Colors</h2>
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
            <ColorField label="Primary / Accent" fieldKey="primary_color" />
            <ColorField label="Accent Secondary" fieldKey="accent_color" />
            <ColorField label="Sidebar Background" fieldKey="sidebar_bg" />
            <ColorField label="Sidebar Border" fieldKey="sidebar_border" />
            <ColorField label="Sidebar Text" fieldKey="sidebar_text" />
            <ColorField label="Sidebar Active" fieldKey="sidebar_active" />
          </div>
          <div className="mt-4 p-4 rounded-sm" style={{ backgroundColor: form.sidebar_bg }}>
            <p className="text-xs mb-2" style={{ color: form.sidebar_text }}>Sidebar Preview:</p>
            <div className="flex items-center gap-2 px-3 py-2" style={{ backgroundColor: form.sidebar_active, color: "#fff" }}>
              <span className="text-sm font-medium">Active Item</span>
            </div>
            <div className="flex items-center gap-2 px-3 py-2" style={{ color: form.sidebar_text }}>
              <span className="text-sm">Inactive Item</span>
            </div>
          </div>
        </div>

        <Button onClick={handleSaveBranding} disabled={saving} className="bg-[#D97706] hover:bg-[#B45309] text-white rounded-none px-8" data-testid="save-branding-button">
          <Save size={16} className="mr-2" />{saving ? "Saving..." : "Save Branding Settings"}
        </Button>

        <BlueprintTypeSection types={bpTypes} onAdd={openAddBpType} onEdit={openEditBpType} onDelete={handleDeleteBpType} />
      </div>

      {/* Blueprint Type Dialog */}
      <Dialog open={bpTypeDialogOpen} onOpenChange={setBpTypeDialogOpen}>
        <DialogContent className="bg-white rounded-none border border-[#E7E5E4]" aria-describedby="bp-type-form-desc">
          <DialogHeader><DialogTitle className="font-heading font-bold text-[#1C1917]">{editingBpType ? "Edit Blueprint Type" : "Add Blueprint Type"}</DialogTitle></DialogHeader>
          <div id="bp-type-form-desc" className="sr-only">Form to manage blueprint types</div>
          <form onSubmit={handleBpTypeSubmit} className="space-y-4">
            <div>
              <Label className="text-[#1C1917]">Type Name *</Label>
              <Input value={bpTypeForm.name} onChange={(e) => setBpTypeForm({ ...bpTypeForm, name: e.target.value })} required className="mt-1 rounded-none border-[#E7E5E4]" placeholder="Mechanical" data-testid="bp-type-name-input" />
            </div>
            <Button type="submit" className="w-full bg-[#D97706] hover:bg-[#B45309] text-white rounded-none" data-testid="submit-bp-type-button">{editingBpType ? "UPDATE" : "CREATE"}</Button>
          </form>
        </DialogContent>
      </Dialog>
    </Layout>
  );
}
